import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { PhotoFile, UiKitInputPhoto } from '@/fo/ui/kit/input/photo';
import { getMediaBaseUrl } from '@/fo/util/image-loader';

import { uploadReviewImage } from '../../api/uploadImages';

import { styles } from './PhotoField.styles';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export interface PhotoFieldProps {
  name?: string;
  onChange?: (value: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export function PhotoField({
  name,
  onChange,
  onLoadingChange
}: PhotoFieldProps) {
  const slots = styles();

  const t = useTranslations('review.form.photo');

  const [files, setFiles] = useState<PhotoFile[]>([]);

  const uploadImages = async (files: File[]) => {
    onLoadingChange?.(true);

    const photoFilesPromises = files.map(async (file) => {
      const { error, data } = await uploadReviewImage(file);

      if (error || !data?.filename || !data?.url) {
        return null;
      }

      return {
        filename: data.filename,
        previewUrl: getMediaBaseUrl() + '/review_images/tmp/' + data.filename
      };
    });

    const photoFiles = await Promise.all(photoFilesPromises);

    const uploadedFiles = photoFiles.filter((file) => file !== null);

    setFiles((prev) => {
      const newFiles = [...prev, ...uploadedFiles];
      onChange?.(newFiles.map((file) => file.filename).join(','));
      onLoadingChange?.(false);
      return newFiles;
    });
  };

  const handleSelect = (files: File[] | null) => {
    if (!files) {
      setFiles([]);
      onChange?.('');
      return;
    }

    uploadImages(files);
  };

  const handleRemove = (file: PhotoFile) => {
    setFiles(files.filter((f) => f.filename !== file.filename));
    onChange?.(files.map((file) => file.filename).join(','));
  };

  return (
    <div className={slots.base()}>
      <UiKitInputPhoto
        maxSize={MAX_FILE_SIZE}
        title={t('title')}
        helperText={t('helperText')}
        multiple
        value={files}
        onSelect={handleSelect}
        onRemove={handleRemove}
        maxCount={5}
      />
      <input
        type="hidden"
        name={name}
        value={files.map((file) => file.filename).join(', ')}
      />
    </div>
  );
}
